import React, { Component } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  Input,
  InputLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import Filter9PlusIcon from '@mui/icons-material/Filter9Plus';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { toast } from 'react-toastify';

import ServiceCommandUnit from './ServiceCommandUnit';
import ManageReportDataApi from '../../Api/ApiManageReportData';
import appdata from '../../Services/appdata';
import './style.css';

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

const getItemStyle = (
  isDragging,
  draggableStyle,
  selectedAccount,
  accountId,
) => ({
  userSelect: 'none',
  background: isDragging
    ? 'lightgreen'
    : selectedAccount === accountId
    ? 'grey'
    : '#e5e5e5',
  border: '1px solid #c0c0c0',
  ...draggableStyle,
});

const getListStyle = (isDraggingOver) => ({
  background: isDraggingOver ? 'lightblue' : 'white',
  cursor: 'pointer',
  overflow: 'auto auto',
});

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

class BalanceSheetLayout extends Component {
  constructor(props) {
    super(props);
    this.state = {
      companies: [],
      selectedCompany: -1,
      rows: [],
      accountTypes: [],
      accountSubTypes: [],
      usedBalanceSheetAccounts: [],
      usedAccounts: [],
      usedSubAccounts: [],
      selectedGroup: -1,
      selectedAccount: -1,
      selectedSubAccount: -1,
      selectedParent: -1,
      accountDialogOpen: false,
      accountSubDialogOpen: false,
      deleteDialogOpen: false,
      newAccounts: [],
      newSubAccounts: [],
    };
    this.onDragEnd = this.onDragEnd.bind(this);
  }

  async componentDidMount() {
    const company_org_id = appdata.getItem('company_org_id');
    let comps = [];
    await Promise.all([
      ManageReportDataApi.getCompanies()
        .then((res) => {
          if (res.status === 200) comps = res.data;
          else toast.error(res.message);
        })
        .catch((error) => toast.error(error.message)),
      this.props.getTemplateData('BalanceSheetLayout', company_org_id),
    ]);
    this.setState({
      companies: comps,
      selectedCompany: company_org_id,
    });
    
    requestAnimationFrame(function () {
      const event = new UIEvent('resize', {
        bubbles: true,
        cancelable: false,
        view: window
      });
      window.dispatchEvent(event);
    });
  }

  componentWillUnmount() {
    this.setState({ rows: [] });
  }

  async componentDidUpdate(prevProps) {
    if (this.props.rows && prevProps.rows !== this.props.rows) {
      let rows = this.props.rows[0];
      this.getUsedBlanaceSheetAccounts(rows);

      this.setState({
        rows,
        accountTypes: this.props.rows[1].sort((a, b) =>
          a.AccountTypeCode < b.AccountTypeCode ? -1 : 1,
        ),
        accountSubTypes: this.props.rows[2].sort((a, b) =>
          a.AccountSubTypeCode < b.AccountSubTypeCode ? -1 : 1,
        ),
      });
    }
  }

  getUsedBlanaceSheetAccounts = (rows) => {
    let usedBalanceSheetAccounts = [];

    rows.forEach((row, index) => {
      row.subItems.forEach((type) => {
        let usedAccount = {
          group: index,
          account: type.content,
          subAccounts: [],
        };
        type.subItems.forEach((subtype) =>
          usedAccount.subAccounts.push(subtype.content),
        );
        usedBalanceSheetAccounts.push(usedAccount);
      });
    });

    this.setState({
      usedBalanceSheetAccounts,
    });
  };

  handleSelectCompany = async (ev) => {
    appdata.setItem('company_org_id', ev.target.value);
    this.setState({ selectedCompany: ev.target.value });
    await this.props.getTemplateData('BalanceSheetLayout', ev.target.value);
  };

  async onDragEnd(result, idx) {
    if (!result.destination) {
      return;
    }
    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;
    if (result.type === 'droppableItem') {
      const rows = reorder(
        this.state.rows[idx].subItems,
        sourceIndex,
        destIndex,
      );
      let result_rows = this.state.rows;
      result_rows[idx].subItems = rows;

      this.setState({
        rows: result_rows,
      });
    } else if (result.type === 'droppableSubItem') {
      const itemSubItemMap = this.state.rows[idx].subItems.reduce(
        (acc, item) => {
          acc[item.id] = item.subItems;
          return acc;
        },
        {},
      );

      const sourceParentId = parseInt(result.source.droppableId);
      const destParentId = parseInt(result.destination.droppableId);

      const sourceSubItems = itemSubItemMap[sourceParentId];
      const destSubItems = itemSubItemMap[destParentId];

      let newItems = [...this.state.rows[idx].subItems];

      if (sourceParentId === destParentId) {
        const reorderedSubItems = reorder(
          sourceSubItems,
          sourceIndex,
          destIndex,
        );

        newItems = newItems.map((item) => {
          if (item.id === sourceParentId) {
            item.subItems = reorderedSubItems;
          }
          return item;
        });

        let result_rows = this.state.rows;
        result_rows[idx].subItems = newItems;

        this.setState({
          rows: result_rows,
        });

        ManageReportDataApi.updateTemplate(this.props.name, JSON.stringify(result_rows))
          .then((res) => {
            if (res.status === 500) toast.error(res.message);
          })
          .catch((error) => toast.error(error.message));
      } else {
        let newSourceSubItems = [...sourceSubItems];
        const [draggedItem] = newSourceSubItems.splice(sourceIndex, 1);

        let newDestSubItems = [...destSubItems];
        newDestSubItems.splice(destIndex, 0, draggedItem);

        newItems = newItems.map((item) => {
          if (item.id === sourceParentId) {
            item.subItems = newSourceSubItems;
          } else if (item.id === destParentId) {
            item.subItems = newDestSubItems;
          }
          return item;
        });

        let result_rows = this.state.rows;
        result_rows[idx].subItems = newItems;

        this.setState({
          rows: result_rows,
        });

        await ManageReportDataApi.updateTemplate(
          this.props.name,
          JSON.stringify(result_rows),
        );
      }
    }
  }

  handleSelectGroup = (event) => {
    let usedAccounts = [];
    this.state.usedBalanceSheetAccounts.forEach((group) => {
      if (group.group === parseInt(event.target.value)) {
        usedAccounts.push(group.account);
        return usedAccounts;
      }
    });
    this.setState({
      selectedGroup: parseInt(event.target.value),
      usedAccounts,
    });
  };

  handleSelectAccountType(event) {
    this.setState({
      newAccounts: event.target.value,
    });
  }

  handleAddNewAccounts = async () => {
    let group = this.state.rows[this.state.selectedGroup];
    let maxId = group.subItems.reduce(
      (acc, shot) => (acc = acc > shot.id ? acc : shot.id),
      0,
    );

    this.state.newAccounts.map((account, index) =>
      group.subItems.push({
        id:
          maxId !== 0
            ? maxId + index + 1
            : this.state.selectedGroup * 100 + index + 1,
        content: account,
        subItems: [],
      }),
    );

    let result_rows = this.state.rows;
    result_rows[this.state.selectedGroup] = group;

    this.getUsedBlanaceSheetAccounts(result_rows);

    this.setState({
      rows: result_rows,
    });

    await ManageReportDataApi.updateTemplate(
      this.props.name,
      JSON.stringify(this.state.rows),
    );
    this.handleDialogClose();
  };

  handleClickAddSubAccount = () => {
    if (this.state.selectedGroup === -1)
      toast.warning(
        'Please select the account that insert the sub account.',
      );
    else
      this.setState({
        accountSubDialogOpen: true,
      });
  };

  handleToggleAccount = (groupId, account) => {
    if (account.id !== this.state.selectedAccount) {
      let usedSubAccounts = [];
      this.state.usedBalanceSheetAccounts.forEach((group) => {
        if (group.group === groupId && group.account === account.content) {
          account.subItems.forEach((subaccount) =>
            usedSubAccounts.push(subaccount.content),
          );
          return usedSubAccounts;
        }
      });

      this.setState({
        selectedGroup: groupId,
        selectedAccount: account.id,
        usedSubAccounts,
      });
    } else {
      this.setState({
        selectedGroup: -1,
        selectedAccount: -1,
      });
    }
  };

  handleSelectSubAccountTypes(event) {
    this.setState({
      newSubAccounts: event.target.value,
    });
  }

  handleAddNewSubAccount = async () => {
    if (this.state.newSubAccounts.length === 0)
      toast.warning('Please select the new sub accounts.');
    else {
      let result_rows = this.state.rows;
      let group = result_rows[this.state.selectedGroup];

      let targetAccount = group.subItems.find(
        (account) => account.id === this.state.selectedAccount,
      );
      let maxId = targetAccount.subItems.reduce(
        (acc, shot) => (acc = acc > shot.id ? acc : shot.id),
        0,
      );

      this.state.newSubAccounts.forEach((account, index) =>
        targetAccount.subItems.push({
          id:
            maxId !== 0
              ? maxId + index + 1
              : this.state.selectedAccount * 10 + index + 1,
          content: account,
        }),
      );

      group.subItems.map((account) =>
        account.id === this.state.selectedAccount
          ? (account = targetAccount)
          : account,
      );
      result_rows[this.state.selectedGroup] = group;
      this.getUsedBlanaceSheetAccounts(result_rows);

      this.setState({
        rows: result_rows,
      });
      await ManageReportDataApi.updateTemplate(
        this.props.name,
        JSON.stringify(this.state.rows),
      );
      this.handleDialogClose();
    }
  };

  handleToggleSubAccount = (e, groupId, accountId, subAccountId) => {
    e.preventDefault();
    e.stopPropagation();
    if (subAccountId !== this.state.selectedSubAccount) {
      this.setState({
        selectedGroup: groupId,
        selectedAccount: -1,
        selectedParent: accountId,
        selectedSubAccount: subAccountId,
      });
    } else {
      this.setState({
        selectedGroup: -1,
        selectedAccount: -1,
        selectedParent: -1,
        selectedSubAccount: -1,
      });
    }
  };

  handleClickDeleteAccount = () => {
    if (
      this.state.selectedAccount === -1 &&
      this.state.selectedSubAccount === -1
    )
      toast.warning(
        'Please select the account or subaccount that have to delete.',
      );
    else {
      this.setState({
        deleteDialogOpen: true,
      });
    }
  };

  handleDeleteItem = async () => {
    if (this.state.selectedAccount !== -1) {
      let result_rows = this.state.rows;
      let account_types = result_rows[this.state.selectedGroup];
      let updated_accounts = account_types.subItems.filter(
        (item) => item.id !== this.state.selectedAccount,
      );
      account_types.subItems = updated_accounts;
      result_rows[this.state.selectedGroup] = account_types;
      this.getUsedBlanaceSheetAccounts(result_rows);

      this.setState({ rows: result_rows });
      await ManageReportDataApi.updateTemplate(
        this.props.name,
        JSON.stringify(result_rows),
      );
    } else {
      let result_rows = this.state.rows;
      let account_types = result_rows[this.state.selectedGroup];
      let sub_account_types = account_types.subItems.find(
        (item) => item.id === this.state.selectedParent,
      );
      let updated_sub_accounts = sub_account_types.subItems.filter(
        (item) => item.id !== this.state.selectedSubAccount,
      );
      sub_account_types.subItems = updated_sub_accounts;

      let updated_accounts = account_types.subItems.filter((item) =>
        item.id === this.state.selectedParent ? sub_account_types : item,
      );
      account_types.subItems = updated_accounts;
      result_rows[this.state.selectedGroup] = account_types;
      this.getUsedBlanaceSheetAccounts(result_rows);

      this.setState({
        rows: result_rows,
      });
      await ManageReportDataApi.updateTemplate(
        this.props.name,
        JSON.stringify(result_rows),
      );
    }
    this.handleDialogClose();
  };

  handleDialogClose = () => {
    this.setState({
      selectedGroup: -1,
      selectedAccount: -1,
      selectedSubAccount: -1,
      selectedParent: -1,
      accountSubDialogOpen: false,
      accountDialogOpen: false,
      deleteDialogOpen: false,
      newAccounts: [],
      newSubAccounts: [],
    });
  };

  render() {
    const {
      companies,
      selectedCompany,
      accountSubDialogOpen,
      accountDialogOpen,
      deleteDialogOpen,
      selectedGroup,
      accountTypes,
      accountSubTypes,
      usedAccounts,
      usedSubAccounts,
      newAccounts,
      newSubAccounts,
      rows,
    } = this.state;

    return (
      <>
        <Grid
          container
          style={{
            border: '1px solid #e7eaec',
            backgroundColor: 'white',
            height: 48,
            position: 'fixed',
            width: '74%',
            zIndex: 1,
          }}
        >
          <Grid size={6}>
            <label style={{ marginLeft: 8, marginTop: 12 }}>Company : </label>
            <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }}>
              <Select
                value={selectedCompany}
                onChange={this.handleSelectCompany}
              >
                <MenuItem value={-1} disabled>
                  {' '}
                </MenuItem>
                {companies &&
                  companies.map(
                    (cur) =>
                      cur.IsActive && (
                        <MenuItem key={cur.id} value={cur.id}>
                          {cur.CompanyName}
                        </MenuItem>
                      ),
                  )}
              </Select>
            </FormControl>
          </Grid>
          {selectedCompany !== -1 ? (
            <Grid
              size={6}
              style={{ display: 'flex', justifyContent: 'flex-end' }}
            >
              <Tooltip title="Add Sub Account">
                <IconButton
                  aria-label="subaccount-add"
                  onClick={this.handleClickAddSubAccount}
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Add Account">
                <IconButton
                  aria-label="account-add"
                  onClick={() =>
                    this.setState({
                      accountDialogOpen: true,
                    })
                  }
                >
                  <Filter9PlusIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  aria-label="Delete"
                  onClick={this.handleClickDeleteAccount}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          ) : null}
        </Grid>

        <Grid size={12} style={{ height: '89vh', overflowY: 'auto' }}>
          {rows.map((row, idx) => (
            <React.Fragment key={idx}>
              <p style={{ marginTop: idx === 0 ? 63 : 15 }}>{row.content}</p>
              <DragDropContext
                onDragEnd={(event) => this.onDragEnd(event, idx)}
              >
                <Droppable droppableId={idx.toString()} type="droppableItem">
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      style={getListStyle(snapshot.isDraggingOver)}
                    >
                      <div>
                        {row.subItems.map((item, index) => (
                          <Draggable
                            key={item.id}
                            draggableId={item.id.toString()}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <div>
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  style={getItemStyle(
                                    snapshot.isDragging,
                                    provided.draggableProps.style,
                                    this.state.selectedAccount,
                                    item.id,
                                  )}
                                  onClick={() =>
                                    this.handleToggleAccount(idx, item)
                                  }
                                >
                                  <span
                                    {...provided.dragHandleProps}
                                    style={{
                                      display: 'inline-block',
                                      margin: '13px 10px 9px 20px',
                                      width: 30,
                                      verticalAlign: 'middle',
                                    }}
                                  >
                                    <DragIndicatorIcon />
                                  </span>
                                  <TextField
                                    autoFocus
                                    disabled={true}
                                    value={item.content}
                                    size="small"
                                    variant="standard"
                                  />
                                  <ServiceCommandUnit
                                    subItems={item.subItems}
                                    type={item.id}
                                    groupId={idx}
                                    selectedAccount={
                                      this.state.selectedSubAccount
                                    }
                                    handleToggle={this.handleToggleSubAccount}
                                  />
                                </div>
                                {provided.placeholder}
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </React.Fragment>
          ))}
        </Grid>

        <Dialog
          open={accountDialogOpen}
          onClose={() => this.handleDialogClose()}
          aria-labelledby="add-account-dialog"
        >
          <DialogTitle id="add-account-dialog">Add Accounts</DialogTitle>
          <DialogContent>
            <DialogContentText>
              To add the account in the group, please select the group name
              here.
            </DialogContentText>
            <Grid
              size={12}
              style={{ display: 'flex', justifyContent: 'center' }}
            >
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  aria-label="position"
                  name="group"
                  defaultValue="top"
                  onChange={this.handleSelectGroup}
                  value={selectedGroup}
                >
                  <FormControlLabel
                    value={0}
                    control={<Radio color="primary" />}
                    label="Assets"
                  />
                  <FormControlLabel
                    value={1}
                    control={<Radio color="primary" />}
                    label="Liabilities"
                  />
                  <FormControlLabel
                    value={2}
                    control={<Radio color="primary" />}
                    label="Nett Assets"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid size={12}>
              <InputLabel id="select-account-label">Account Types</InputLabel>
              <Select
                multiple
                labelId="select-account-label"
                id="select-account"
                input={<Input />}
                fullWidth
                value={newAccounts}
                MenuProps={MenuProps}
                onChange={(e) => this.handleSelectAccountType(e)}
                disabled={selectedGroup !== -1 ? false : true}
              >
                {accountTypes &&
                  accountTypes.map((account, index) => (
                    <MenuItem
                      key={index}
                      value={account.AccountTypeCode}
                      disabled={
                        usedAccounts.indexOf(account.AccountTypeCode) !== -1
                          ? true
                          : false
                      }
                    >
                      {account.AccountTypeCode}
                    </MenuItem>
                  ))}
              </Select>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              style={{
                backgroundColor: '#e0e0e0',
                color: 'rgba(0, 0, 0, 0.87)',
              }}
              onClick={() => this.handleDialogClose()}
            >
              Cancel
            </Button>
            <Button
              style={{
                backgroundColor: '#1976d2',
                color: '#ffffff',
              }}
              onClick={this.handleAddNewAccounts}
            >
              Add
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={accountSubDialogOpen}
          onClose={() => this.handleDialogClose()}
          aria-labelledby="add-subaccount-dialog"
        >
          <DialogTitle id="add-subaccount-dialog">Add Sub Accounts</DialogTitle>
          <DialogContent>
            <DialogContentText>
              To add the account in the group, please select the group name
              here.
            </DialogContentText>
            <Grid size={12}>
              <InputLabel id="demo-simple-select-account-sub-label">
                Account Sub Type
              </InputLabel>
              <Select
                multiple
                labelId="select-subaccount-label"
                id="select-subaccount"
                value={newSubAccounts}
                style={{ margin: 12 }}
                fullWidth
                input={<Input />}
                MenuProps={MenuProps}
                onChange={(e) => this.handleSelectSubAccountTypes(e)}
              >
                {accountSubTypes &&
                  accountSubTypes.map((subAccount, index) => (
                    <MenuItem
                      key={index}
                      value={subAccount.AccountSubTypeCode}
                      disabled={
                        usedSubAccounts.indexOf(
                          subAccount.AccountSubTypeCode,
                        ) !== -1
                          ? true
                          : false
                      }
                    >
                      {subAccount.AccountSubTypeCode}
                    </MenuItem>
                  ))}
              </Select>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              style={{
                backgroundColor: '#e0e0e0',
                color: 'rgba(0, 0, 0, 0.87)',
              }}
              onClick={() => this.handleDialogClose()}
            >
              Cancel
            </Button>
            <Button
              style={{
                backgroundColor: '#1976d2',
                color: '#ffffff',
              }}
              onClick={this.handleAddNewSubAccount}
            >
              Add
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={deleteDialogOpen}
          onClose={this.handleDialogClose}
          aria-labelledby="delete-dialog"
        >
          <DialogTitle id="delete-dialog">
            Delete selected {selectedGroup !== -1 ? 'Group' : 'Account'}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-slide-description">
              Do you want to delete the selected{' '}
              {selectedGroup !== -1 ? 'group' : 'account'} ?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              style={{
                backgroundColor: '#e0e0e0',
                color: 'rgba(0, 0, 0, 0.87)',
              }}
              onClick={this.handleDialogClose}
            >
              Disagree
            </Button>
            <Button
              style={{
                backgroundColor: '#1976d2',
                color: '#ffffff',
              }}
              onClick={this.handleDeleteItem}
            >
              Agree
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
}

export default BalanceSheetLayout;
